// TODO: Implement localization i18n
import { faPen, faPlus, faSearch, faTrash } from "@fortawesome/free-solid-svg-icons"
import { Modal, Pagination, TextInput, Search, Loading } from "@wfp/ui"
import { GET_ITEM_LIMIT, MARKET_MODULE } from "constants/constants"
import Button from "components/Shared/Button/Button"
import HeaderFilters from "components/Shared/HeaderFilters/HeaderFilters"
import { CommoditiesUnit } from "interfaces/dataInterfaces"
import { useEffect, useState } from "react"
import BaseTable, { AutoResizer, Column, ColumnShape } from "react-base-table"
import { SubmitHandler, useForm, useWatch } from "react-hook-form"
import {
  useGetCommoditiesUnitQuery,
  useAddCommoditiesUnitMutation,
  useUpdateCommoditiesUnitMutation,
  useDeleteCommoditiesUnitMutation,
} from "services/core/commodities"
import useCommon from "hooks/useCommon"
import PermissionWrapper from "components/Shared/Common/PermissionWrapper"

type Inputs = {
  name: string
}
const permissionsProps = {
  moduleId: MARKET_MODULE,
  subModule: "ccpucs",
}
export default function CommoditiesUnitPage() {
  const { showSnackBar } = useCommon()

  const [pageNumber, setPageNumber] = useState(1)
  const [getItemLimit, setGetItemLimit] = useState(GET_ITEM_LIMIT)
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    control,
  } = useForm<Inputs>()
  const name = useWatch({
    control,
    name: "name",
    defaultValue: "",
  })

  const [actionItem, setActionItem] = useState<{
    item: CommoditiesUnit
    action: "edit" | "delete"
  } | null>(null)

  const [query, setQuery] = useState<string | undefined>("")
  const [inputText, setInputText] = useState<string | undefined>(undefined)
  const [showNewUnitModal, setShowNewUnitModal] = useState(false)

  const { data: commoditiesUnit, isFetching: isLoadingUnits } = useGetCommoditiesUnitQuery({
    skip: pageNumber === 1 ? 0 : (pageNumber - 1) * getItemLimit,
    take: getItemLimit,
    query,
  })

  const [addCommoditiesUnit, { isLoading: isLoadingAdd }] = useAddCommoditiesUnitMutation()
  const [updateCommoditiesUnit, { isLoading: isLoadingEdit }] = useUpdateCommoditiesUnitMutation()
  const [deleteCommoditiesUnit, { isLoading: isLoadingDelete }] = useDeleteCommoditiesUnitMutation()

  const onSubmit: SubmitHandler<Inputs> = async data => {
    if (actionItem?.action === "edit") {
      try {
        await updateCommoditiesUnit({
          commodityUnitID: actionItem.item.commodityUnitID,
          commodityUnitName: data.name?.replace(/\s{2,}/g, " ").trim(),
        }).unwrap()
        setActionItem(null)
        showSnackBar("Commodity unit updated successfully!")
      } catch (error) {
        showSnackBar("Commodity unit not updated", "error")
      }
    } else {
      try {
        await addCommoditiesUnit({ commodityUnitName: data.name?.replace(/\s{2,}/g, " ").trim() }).unwrap()
        setShowNewUnitModal(false)
        showSnackBar("Commodity unit added successfully!")
      } catch (error) {
        showSnackBar("Commodity unit not added", "error")
      }
    }
    reset()
  }

  const columns: Array<ColumnShape<CommoditiesUnit>> = [
    {
      key: "commodityUnitID",
      dataKey: "commodityUnitID",
      title: "ID",
      width: 80,
      maxWidth: 80,
    },
    {
      key: "commodityUnitName",
      dataKey: "commodityUnitName",
      title: "Name",
      width: 200,
    },
    {
      key: "action",
      width: 0,
      align: Column.Alignment.CENTER,
      flexGrow: 1,
      cellRenderer: ({ rowData }) => (
        <PermissionWrapper {...permissionsProps} feature="edit">
          <>
            <Button
              action={() =>
                setActionItem({
                  item: rowData,
                  action: "edit",
                })
              }
              label="Edit"
              className="btn-full blue action-btn"
              icon={faPen}
            />

            <Button
              action={() =>
                setActionItem({
                  item: rowData,
                  action: "delete",
                })
              }
              label="Delete"
              className="btn-full danger action-btn"
              style={{ marginLeft: "10px" }}
              icon={faTrash}
            />
          </>
        </PermissionWrapper>
      ),
    },
  ]

  const handleDelete = async (id: number) => {
    try {
      await deleteCommoditiesUnit(id).unwrap()
      setActionItem(null)
      showSnackBar("Commodity unit deleted successfully!")
    } catch (error) {
      showSnackBar("Commodity unit not deleted", "error")
    }
  }

  const handleNewEditModalClose = () => {
    reset()
    actionItem ? setActionItem(null) : setShowNewUnitModal(false)
  }

  const handleSearch = (e: any) => {
    setInputText(e)
  }

  const handleSubmitQuery = () => {
    setPageNumber(1)
    setQuery(inputText)
  }

  const handleSearchKey = (e: any) => {
    inputText && e?.code === "Enter" && handleSubmitQuery()
  }

  useEffect(() => {
    !inputText && query && setQuery("")
  }, [inputText])

  useEffect(() => {
    reset()
    if (actionItem?.action === "edit") {
      setValue("name", actionItem?.item.commodityUnitName)
    }
  }, [actionItem])

  return (
    <>
      {(isLoadingAdd || isLoadingEdit || isLoadingDelete) && <Loading />}
      <HeaderFilters>
        <div className="d-flex" style={{ width: "300px" }}>
          <Search
            id="query"
            name="query"
            placeholder="Type here to filter results..."
            className="text-input mr-2"
            value={query || ""}
            onChange={e => handleSearch(e)}
            onKeyUp={e => handleSearchKey(e)}
          />
          <Button action={handleSubmitQuery} label="Search" className="btn-full blue" icon={faSearch} />
        </div>

        <PermissionWrapper {...permissionsProps} feature="addNew">
          <Button
            action={() => setShowNewUnitModal(true)}
            label="Add new commodity unit"
            className="btn-full green"
            icon={faPlus}
          />
        </PermissionWrapper>
      </HeaderFilters>

      <div
        className={isLoadingUnits ? "mt-2 d-flex align-center justify-center" : "mt-2"}
        style={{ height: "100%", minHeight: "60vh" }}
      >
        {isLoadingUnits ? (
          <Loading withOverlay={false} />
        ) : (
          <AutoResizer>
            {({ width, height }) => (
              <BaseTable
                data={commoditiesUnit?.items}
                width={width}
                height={height}
                rowKey="commodityUnitID"
                className="custom-table"
                rowHeight={38}
                headerHeight={45}
                columns={columns}
                emptyRenderer={() => <p>No records to display</p>}
              />
            )}
          </AutoResizer>
        )}
      </div>

      <Modal
        open={actionItem?.action === "delete"}
        type="danger"
        modalHeading="Confirm Delete"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onRequestSubmit={() => handleDelete(actionItem!.item.commodityUnitID)}
        onSecondarySubmit={() => setActionItem(null)}
        onRequestClose={() => setActionItem(null)}
      >
        Are you sure you want to delete the selected record?
      </Modal>

      <Modal
        open={showNewUnitModal || actionItem?.action === "edit"}
        modalHeading={actionItem?.action === "edit" ? "Modify commodity unit" : "Insert new commodity unit"}
        primaryButtonText="Save"
        secondaryButtonText="Cancel"
        onRequestSubmit={handleSubmit(onSubmit)}
        onSecondarySubmit={handleNewEditModalClose}
        onRequestClose={handleNewEditModalClose}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <TextInput
            labelText="Name"
            placeholder="Commodity unit name"
            defaultValue={actionItem?.item.commodityUnitName}
            invalid={errors.name}
            invalidText={errors.name?.message}
            value={name?.replace(/\s{2,}/g, " ")}
            {...register("name", { required: "Name is required" })}
          />
        </form>
      </Modal>

      <Pagination
        totalItems={commoditiesUnit?.count ?? 0}
        pageSize={getItemLimit}
        pageSizes={[10, 20, 30]}
        page={pageNumber}
        onChange={(event: any): void => {
          setPageNumber((event as any).page)
          setGetItemLimit((event as any).pageSize)
        }}
      />
    </>
  )
}
